/**
 * PickTicketSessionCache - Session-scoped cache for pick ticket printing
 *
 * This cache is designed to:
 * 1. Start fresh on each print session (no stale data)
 * 2. Reuse data within the same print session to reduce API calls
 * 3. Automatically clean up when the print session ends
 *
 * Critical: This cache is session-scoped, NOT persistent. It exists only
 * for the duration of a single print operation to prevent redundant API calls
 * while ensuring data freshness for each new print.
 */

import gql from 'graphql-tag';
import { PURCHASE_ORDER_INFO } from '@/gql/fragments';

export class PickTicketSessionCache {
    constructor(apolloClient) {
        // Unique session ID to track this print session
        this.sessionId = Date.now();
        this.apollo = apolloClient;

        // Cache stores - cleared on each new session
        this.vendors = new Map();
        this.vendorAddresses = new Map();
        this.purchaseOrders = new Map();
        this.inventoryItems = new Map();
        this.colorOptions = new Map();
        this.poStatuses = new Map();
        this.lineItemPOs = new Map();

        // Track performance metrics
        this.metrics = {
            apiCalls: 0,
            cacheHits: 0,
            sessionStartTime: Date.now()
        };

        console.log(`[PickTicketCache] New session created: ${this.sessionId}`);
    }

    /**
     * Get vendor data with caching
     * @param {number} vendorId - The vendor ID
     * @returns {Promise<Object>} Vendor data
     */
    async getVendor(vendorId) {
        if (!vendorId) return null;

        if (this.vendors.has(vendorId)) {
            this.metrics.cacheHits++;
            console.log(`[PickTicketCache] Cache hit for vendor ${vendorId}`);
            return this.vendors.get(vendorId);
        }

        this.metrics.apiCalls++;
        console.log(`[PickTicketCache] Fetching vendor ${vendorId} from API`);

        try {
            const result = await this.apollo.query({
                query: gql`
                    query vendorById($id: Int!) {
                        response: vendorById(id: $id) {
                            vendorId
                            companyName
                            addresses {
                                vendorAddressId
                                label
                                addressLine1
                                addressLine2
                                addressTypeId
                                city
                                state
                                postalCode
                                isPrimaryAddress
                            }
                            contacts {
                                vendorContactId
                                primaryContact
                                firstName
                                lastName
                                workPhoneNumber
                                cellPhoneNumber
                                email
                                isActive
                            }
                        }
                    }
                `,
                variables: { id: vendorId },
                fetchPolicy: 'network-only' // Always fresh for new session
            });

            const vendor = result.data?.response || null;
            if (vendor) {
                this.vendors.set(vendorId, vendor);
            }
            return vendor;
        } catch (error) {
            console.error(`[PickTicketCache] Error fetching vendor ${vendorId}:`, error);
            return null;
        }
    }

    /**
     * Get vendor address with caching
     * @param {number} vendorId - The vendor ID
     * @returns {Promise<Object>} Primary vendor address
     */
    async getVendorAddress(vendorId) {
        if (!vendorId) return null;

        if (this.vendorAddresses.has(vendorId)) {
            this.metrics.cacheHits++;
            console.log(`[PickTicketCache] Cache hit for vendor address ${vendorId}`);
            return this.vendorAddresses.get(vendorId);
        }

        // Try to get from vendor cache first
        let vendor = this.vendors.get(vendorId);
        if (!vendor) {
            vendor = await this.getVendor(vendorId);
        }

        if (vendor && vendor.addresses) {
            const primaryAddress = vendor.addresses.find(a => a.isPrimaryAddress) || vendor.addresses[0];
            this.vendorAddresses.set(vendorId, primaryAddress);
            return primaryAddress;
        }

        return null;
    }

    /**
     * Get purchase order with status - cached
     * @param {Object} po - Purchase order object
     * @param {number} poId - Purchase order ID
     * @returns {Promise<Object>} Purchase order with status
     */
    async getWithPOStatus(po, poId) {
        const cacheKey = `${poId}`;

        if (this.poStatuses.has(cacheKey)) {
            this.metrics.cacheHits++;
            console.log(`[PickTicketCache] Cache hit for PO status ${poId}`);
            return this.poStatuses.get(cacheKey);
        }

        this.metrics.apiCalls++;
        console.log(`[PickTicketCache] Fetching PO status ${poId} from API`);

        try {
            const result = await this.apollo.query({
                query: gql`
                    ${PURCHASE_ORDER_INFO}
                    query GetPOStatus($id: Int!) {
                        purchaseOrderById(id: $id) {
                            ...PurchaseOrderInfo
                        }
                    }
                `,
                variables: { id: poId },
                fetchPolicy: 'network-only'
            });

            const enrichedPO = {
                ...po,
                ...result.data.purchaseOrderById
            };

            this.poStatuses.set(cacheKey, enrichedPO);
            return enrichedPO;
        } catch (error) {
            console.error(`[PickTicketCache] Error fetching PO status ${poId}:`, error);
            return po;
        }
    }

    /**
     * Get purchase orders by line item ID - cached
     * @param {number} lineItemId - Line item ID
     * @returns {Promise<Array>} Purchase orders for the line item
     */
    async getPurchaseOrderByLineItemId(lineItemId) {
        if (this.lineItemPOs.has(lineItemId)) {
            this.metrics.cacheHits++;
            console.log(`[PickTicketCache] Cache hit for line item POs ${lineItemId}`);
            return this.lineItemPOs.get(lineItemId);
        }

        this.metrics.apiCalls++;
        console.log(`[PickTicketCache] Fetching POs for line item ${lineItemId} from API`);

        try {
            const result = await this.apollo.query({
                query: gql`
                    ${PURCHASE_ORDER_INFO}
                    query GetPurchaseOrderByLineItemId($lineItemId: Long!) {
                        purchaseOrderByLineItemId(lineItemId: $lineItemId) {
                            ...PurchaseOrderInfo
                        }
                    }
                `,
                variables: { lineItemId },
                fetchPolicy: 'network-only'
            });

            // purchaseOrderByLineItemId returns an array directly
            const pos = result.data?.purchaseOrderByLineItemId || [];
            this.lineItemPOs.set(lineItemId, pos);
            return pos;
        } catch (error) {
            console.error(`[PickTicketCache] Error fetching POs for line item ${lineItemId}:`, error);
            return [];
        }
    }

    /**
     * Batch fetch multiple vendors in parallel
     * @param {Array<number>} vendorIds - Array of vendor IDs
     * @returns {Promise<Map>} Map of vendor ID to vendor data
     */
    async batchFetchVendors(vendorIds) {
        const uncachedIds = vendorIds.filter(id => !this.vendors.has(id));

        if (uncachedIds.length === 0) {
            console.log(`[PickTicketCache] All ${vendorIds.length} vendors cached`);
            return new Map(vendorIds.map(id => [id, this.vendors.get(id)]));
        }

        console.log(`[PickTicketCache] Batch fetching ${uncachedIds.length} vendors`);

        try {
            // Since the API doesn't support batch fetching vendors, fetch them individually
            const fetchPromises = uncachedIds.map(async (vendorId) => {
                try {
                    const vendor = await this.getVendor(vendorId);
                    if (vendor) {
                        // Also cache primary address
                        const primaryAddr = vendor.addresses?.find(a => a.isPrimaryAddress) || vendor.addresses?.[0];
                        if (primaryAddr) {
                            this.vendorAddresses.set(vendor.vendorId, primaryAddr);
                        }
                        return [vendorId, vendor];
                    }
                    return [vendorId, null];
                } catch (error) {
                    console.error(`[PickTicketCache] Error fetching vendor ${vendorId}:`, error);
                    return [vendorId, null];
                }
            });

            const vendorResults = await Promise.all(fetchPromises);
            vendorResults.forEach(([id, vendor]) => {
                if (vendor) {
                    this.vendors.set(id, vendor);
                }
            });

            return new Map(vendorIds.map(id => [id, this.vendors.get(id)]));
        } catch (error) {
            console.error('[PickTicketCache] Error batch fetching vendors:', error);
            return new Map();
        }
    }

    /**
     * Batch fetch multiple purchase orders in parallel
     * Since the API doesn't support true batch fetching, we'll fetch in parallel
     * @param {Array<number>} lineItemIds - Array of line item IDs
     * @returns {Promise<Map>} Map of line item ID to purchase orders
     */
    async batchFetchPurchaseOrders(lineItemIds) {
        const uncachedIds = lineItemIds.filter(id => !this.lineItemPOs.has(id));

        if (uncachedIds.length === 0) {
            console.log(`[PickTicketCache] All ${lineItemIds.length} line item POs cached`);
            return new Map(lineItemIds.map(id => [id, this.lineItemPOs.get(id)]));
        }

        console.log(`[PickTicketCache] Fetching POs for ${uncachedIds.length} line items in parallel`);

        // Fetch all uncached POs in parallel (limited to 5 concurrent requests)
        const batchSize = 5;
        const results = new Map();

        for (let i = 0; i < uncachedIds.length; i += batchSize) {
            const batch = uncachedIds.slice(i, i + batchSize);
            const batchPromises = batch.map(lineItemId =>
                this.getPurchaseOrderByLineItemId(lineItemId)
                    .then(pos => ({ lineItemId, pos }))
                    .catch(error => {
                        console.error(`[PickTicketCache] Error fetching PO for line item ${lineItemId}:`, error);
                        return { lineItemId, pos: [] };
                    })
            );

            const batchResults = await Promise.all(batchPromises);
            batchResults.forEach(({ lineItemId, pos }) => {
                results.set(lineItemId, pos);
            });
        }

        // Include cached items in the results
        lineItemIds.forEach(id => {
            if (!results.has(id) && this.lineItemPOs.has(id)) {
                results.set(id, this.lineItemPOs.get(id));
            }
        });

        return results;
    }

    /**
     * Get performance metrics for this session
     * @returns {Object} Performance metrics
     */
    getMetrics() {
        const sessionDuration = Date.now() - this.metrics.sessionStartTime;
        const cacheHitRate = this.metrics.cacheHits / (this.metrics.apiCalls + this.metrics.cacheHits) * 100;

        return {
            sessionId: this.sessionId,
            sessionDuration: sessionDuration,
            apiCalls: this.metrics.apiCalls,
            cacheHits: this.metrics.cacheHits,
            cacheHitRate: cacheHitRate.toFixed(2) + '%',
            totalRequests: this.metrics.apiCalls + this.metrics.cacheHits
        };
    }

    /**
     * Clear all cached data and reset metrics
     * Called when component unmounts or new print session starts
     */
    destroy() {
        const metrics = this.getMetrics();
        console.log('[PickTicketCache] Session destroyed:', metrics);

        // Clear all cache maps
        this.vendors.clear();
        this.vendorAddresses.clear();
        this.purchaseOrders.clear();
        this.inventoryItems.clear();
        this.colorOptions.clear();
        this.poStatuses.clear();
        this.lineItemPOs.clear();

        // Reset metrics
        this.metrics = {
            apiCalls: 0,
            cacheHits: 0,
            sessionStartTime: 0
        };
    }
}

export default PickTicketSessionCache;